import { Fragment, useState } from 'react';
import { Badge } from '../../components/Badge';
import { planLabel } from './labels';
import type { LearningPlan, PlanOffer } from '../../types/learning-plans';

const money = (n: number, currency: string) =>
  /^[A-Z]{3}$/.test(currency)
    ? new Intl.NumberFormat('ar-SA-u-nu-latn', { style: 'currency', currency }).format(n / 100)
    : `${(n / 100).toFixed(2)} ${currency}`;

type Named = { id: string; nameEn: string; nameAr?: string };
const nameOf = (items: Named[], id: string) => {
  const item = items.find((x) => x.id === id);
  return item?.nameAr || item?.nameEn || id;
};

/**
 * The plans on offer as a table: name, grades, price and whether families can
 * buy it. Links, versions and delivery terms are kept under each row's details.
 */
export function OffersTable({ offers, requests, grades, subjects, busy, onCreate, onEdit, onAction }: {
  offers: PlanOffer[];
  requests: LearningPlan[];
  grades: Named[];
  subjects: Named[];
  busy: boolean;
  onCreate: () => void;
  onEdit: (offer: PlanOffer) => void;
  onAction: (offer: PlanOffer, kind: 'publish' | 'withdraw' | 'delete') => void;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const latest = offers.filter((o) => !offers.some((other) => other.slug === o.slug && other.version > o.version));
  const term = search.trim().toLocaleLowerCase();
  const shown = (showHistory ? offers : offers.filter((o) => o.published || latest.includes(o)))
    .filter((o) => !status || (status === 'published' ? o.published : !o.published))
    .filter((o) => [o.nameAr, o.nameEn, o.slug].some((value) => value.toLocaleLowerCase().includes(term)));

  if (offers.length === 0) {
    return (
      <div className="panel space-y-2 p-6">
        <h2 className="font-semibold">أنشئ أول خطة تعليمية</h2>
        <p className="text-sm text-ink-2">اختر نوع الخطة والصفوف، ثم الدروس والأسعار، واحفظها كمسودة أو انشرها.</p>
        <button type="button" className="btn btn-primary" onClick={onCreate}>إضافة خطة</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <section className="panel flex flex-wrap items-end gap-3 p-3" aria-label="تصفية الخطط المعروضة">
        <label className="block min-w-48 flex-1 text-sm">
          <span className="sr-only">البحث عن خطة</span>
          <input className="input" type="search" value={search} aria-label="البحث عن خطة" placeholder="ابحث باسم الخطة"
            onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select className="input w-auto" aria-label="حالة العرض" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">كل الحالات</option>
          <option value="published">منشورة</option>
          <option value="unpublished">غير منشورة</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />
          إظهار جميع الإصدارات السابقة
        </label>
        <button type="button" className="btn btn-primary ms-auto" onClick={onCreate}>إضافة خطة</button>
      </section>

      {shown.length === 0 ? (
        <p role="status" className="panel p-6 text-center text-ink-2">لا توجد خطط مطابقة. غيّر البحث أو حالة العرض.</p>
      ) : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs text-ink-3">
              <tr>
                <th className="px-3 py-2 text-start font-medium">الخطة</th>
                <th className="hidden px-3 py-2 text-start font-medium md:table-cell">الصفوف</th>
                <th className="hidden px-3 py-2 text-start font-medium sm:table-cell">السعر</th>
                <th className="px-3 py-2 text-start font-medium">الحالة</th>
                <th className="px-3 py-2"><span className="sr-only">الإجراءات</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {shown.map((o) => {
                const expanded = open === o.id;
                const linked = requests.some((r) => r.offer.id === o.id);
                const isLatest = latest.includes(o);
                return (
                  <Fragment key={o.id}>
                    <tr data-offer-id={o.id} className={expanded ? 'bg-surface-2' : undefined}>
                      <td className="px-3 py-2.5">
                        <button type="button" className="text-start" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : o.id)}>
                          <span className="block font-medium text-ink">{o.nameAr || o.nameEn}</span>
                          <span className="block text-xs text-ink-3">{planLabel(o.category)} · {o.tracks.length} {o.tracks.length === 1 ? 'مسار' : 'مسارات'}</span>
                        </button>
                      </td>
                      <td className="hidden px-3 py-2.5 text-ink-2 md:table-cell">
                        {o.gradeIds.length <= 2 ? o.gradeIds.map((id) => nameOf(grades, id)).join('، ') : `${o.gradeIds.length} صفوف`}
                      </td>
                      <td className="hidden px-3 py-2.5 tabular-nums sm:table-cell">
                        {money(o.priceCents, o.currency)}
                        <span className="block text-xs text-ink-3">التجربة {money(o.trialPriceCents, o.currency)}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="flex flex-wrap gap-1">
                          <Badge tone={o.published ? 'good' : 'neutral'}>{o.published ? 'منشورة' : 'مسودة'}</Badge>
                          {!isLatest && <Badge>إصدار سابق</Badge>}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => onEdit(o)}>تعديل الخطة</button>
                          <button type="button" className={o.published ? 'btn btn-secondary' : 'btn btn-primary'} disabled={busy}
                            onClick={() => onAction(o, o.published ? 'withdraw' : 'publish')}>
                            {o.published ? 'سحب العرض' : 'مراجعة ونشر'}
                          </button>
                          {!o.published && (
                            <button type="button" className="btn btn-secondary text-danger" disabled={busy || linked}
                              title={linked ? 'هذا الإصدار مرتبط بطلبات طلاب ولا يمكن حذفه.' : 'حذف إصدار غير منشور ولا يرتبط بطلبات طلاب'}
                              onClick={() => onAction(o, 'delete')}>حذف الإصدار</button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr data-offer-details={o.id} className="bg-surface-2">
                        <td colSpan={5} className="px-3 pb-4 pt-1">
                          <div className="space-y-2 rounded-lg border border-rule bg-surface p-3 text-sm">
                            {o.nameEn && <p dir="ltr" lang="en" className="text-start text-ink-2">{o.nameEn}</p>}
                            <p>الصفوف: {o.gradeIds.map((id) => nameOf(grades, id)).join('، ')}</p>
                            {o.tracks.map((t) => (
                              <div key={t.key}>
                                <p className="font-medium">{t.name}: {t.weeklyMeetings} أسبوعيًا · {t.meetings} حصة للدورة · {t.durationMinutes === t.maxDurationMinutes ? t.durationMinutes : `${t.durationMinutes}–${t.maxDurationMinutes}`} دقيقة</p>
                                <p className="text-ink-2">{t.subjectIds.map((id) => nameOf(subjects, id)).join('، ')} · التجربة {t.trialDurationMinutes} دقيقة · {t.changeAllowance} تعديلات · مستحقات المعلّم: {t.compensation}</p>
                              </div>
                            ))}
                            <p className="text-ink-2">مهلة الحجز {o.policy.holdHours} ساعة · إشعار التعديل {o.policy.noticeHours} ساعة · مهلة التعويض {o.policy.makeupDays} أيام</p>
                            <p className="text-xs text-ink-3">الإصدار {o.version} · <span dir="ltr">{o.slug}</span> · تبقى شروط الطلبات الحالية كما اشترتها الأسرة، ولا يُحذف إصدار مرتبط بطلب.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
